/**
 * Build the coach chrono player list for Off-table Record chrono mode.
 *
 * Order matches Live Training subgroup display:
 * 1. Seated players — table number ascending, then Player1 → Player2 → Player3
 * 2. Remaining present subgroup roster players — ranking points DESC
 *    (same default as the table setup wizard / assign-by-ranking)
 *
 * Unseated present players are included so Off-table / physical blocks work
 * without requiring table seats. When the exercise is station-scoped and
 * tables are tagged, players seated on other stations are excluded.
 */

import { playerDisplayName } from "./liveTrainingPlayerContext";
import {
  filterPresentRoster,
  sortRosterByRanking,
} from "./coachSessionTables";

function slotKey(slot) {
  return String(slot || "").toLowerCase();
}

function positionRank(slot) {
  const s = slotKey(slot);
  if (s === "player1" || s === "player_1") return 1;
  if (s === "player2" || s === "player_2") return 2;
  if (s === "player3" || s === "player_3") return 3;
  return 99;
}

function playerIdsFromRow(player) {
  const playerId =
    player?.playerId != null
      ? String(player.playerId)
      : player?.player_id != null
        ? String(player.player_id)
        : null;
  const userId =
    player?.userId != null
      ? String(player.userId)
      : player?.user_id != null
        ? String(player.user_id)
        : null;
  return { playerId, userId, dedupe: playerId || userId };
}

function tableStationId(table) {
  const id = table?.tsgId ?? table?.tsg_id ?? null;
  return id == null || id === "" ? null : String(id);
}

function collectSeatedDedupeKeys(table) {
  const keys = [];
  for (const player of [
    table.player1,
    table.player2,
    table.player3,
    ...(table.extras || []),
  ]) {
    if (!player) continue;
    const { dedupe } = playerIdsFromRow(player);
    if (dedupe) keys.push(dedupe);
  }
  return keys;
}

/**
 * @param {Array<object>} tables - coach table rows (formatCoachTableRow shape)
 * @param {{ presentRoster?: Array<object>, roster?: Array<object>, tsgId?: string|null }} [options]
 * @returns {Array<{ key: string, playerId: string|null, userId: string|null, name: string, tableNumber: number|null, seat: string }>}
 */
export function buildOrderedChronoPlayers(tables = [], options = {}) {
  const presentRoster = filterPresentRoster(
    options?.presentRoster ?? options?.roster ?? []
  );
  const tsgId =
    options?.tsgId != null && options.tsgId !== ""
      ? String(options.tsgId)
      : null;
  const allTables = tables || [];

  const stationTagged = tsgId
    ? allTables.filter((t) => tableStationId(t) === tsgId)
    : [];
  const stationScoped = Boolean(tsgId && stationTagged.length > 0);
  const scopedTables = stationScoped ? stationTagged : allTables;

  const otherStationSeated = new Set();
  if (stationScoped) {
    for (const table of allTables) {
      if (tableStationId(table) === tsgId) continue;
      if (tableStationId(table) == null) continue;
      for (const key of collectSeatedDedupeKeys(table)) {
        otherStationSeated.add(key);
      }
    }
  }

  const out = [];
  const seen = new Set();

  const sorted = [...scopedTables].sort(
    (a, b) =>
      (Number(a.tableNumber ?? a.table_number) || 0) -
      (Number(b.tableNumber ?? b.table_number) || 0)
  );

  for (const table of sorted) {
    const tableNumber =
      table.tableNumber ?? table.table_number ?? null;
    const seats = [
      { seat: "player1", player: table.player1 },
      { seat: "player2", player: table.player2 },
      { seat: "player3", player: table.player3 },
    ].sort((a, b) => positionRank(a.seat) - positionRank(b.seat));

    for (const { seat, player } of seats) {
      if (!player) continue;
      const { playerId, userId, dedupe } = playerIdsFromRow(player);
      if (!dedupe || seen.has(dedupe)) continue;
      seen.add(dedupe);
      out.push({
        key: dedupe,
        playerId,
        userId,
        name: player.name || playerDisplayName(player) || "—",
        tableNumber,
        seat,
      });
    }

    for (const [idx, player] of (table.extras || []).entries()) {
      if (!player) continue;
      const { playerId, userId, dedupe } = playerIdsFromRow(player);
      if (!dedupe || seen.has(dedupe)) continue;
      seen.add(dedupe);
      out.push({
        key: dedupe,
        playerId,
        userId,
        name: player.name || playerDisplayName(player) || "—",
        tableNumber,
        seat: `extra${idx + 1}`,
      });
    }
  }

  // Append present subgroup members who are not seated (typical Off-table).
  // When station-scoped, skip anyone already seated on another station.
  const rankedPresent = sortRosterByRanking(presentRoster, false);
  for (const player of rankedPresent) {
    const { playerId, userId, dedupe } = playerIdsFromRow(player);
    if (!dedupe || seen.has(dedupe)) continue;
    if (otherStationSeated.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({
      key: dedupe,
      playerId,
      userId,
      name: playerDisplayName(player) || "—",
      tableNumber: null,
      seat: "roster",
    });
  }

  return out;
}

export function formatChronoClock(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0);
  const whole = Math.floor(sec);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  const cs = Math.floor((sec - whole) * 100);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}
